import { getIsHeraldSubmission } from '@common/utils';
import { ReactNode } from 'react';
import { LoaderOverlay, Typography } from '@common-components';
import { HeraldApplicationResponse } from 'clients/types';
import { useRetrieveHeraldApplication, useSearchTeamInsuranceProduct } from 'hooks';
import { messages } from 'i18n';
import { TeamInsuranceProduct } from 'types'; // Common types
import { SelectedTeamProductsMap } from 'broker/pages/SubmissionWorkspacePage/components/NestedViews/AddProducts/types';
import { useIsBORFlow } from 'broker/pages/SubmissionWorkspacePage/hooks';
import useSubmissionsWorkspace from 'broker/pages/SubmissionWorkspacePage/store/useSubmissionWorkspace';

interface ProductsWrapperProps {
  children: (props: {
    heraldApplicationResponse?: HeraldApplicationResponse;
    initSelectedProducts: SelectedTeamProductsMap;
    teamProducts: TeamInsuranceProduct[];
    isLoading: boolean;
    onTeamProductsSelected?: (products: TeamInsuranceProduct[]) => Promise<void>;
  }) => ReactNode;
}

export function ProductsWrapper({ children }: ProductsWrapperProps) {
  const { submission } = useSubmissionsWorkspace();

  const { isBOR } = useIsBORFlow();

  const isDynamic = !!(submission && getIsHeraldSubmission(submission));

  const {
    data: heraldApplicationResponse,
    isLoading: isHeraldLoading,
    error,
  } = useRetrieveHeraldApplication({
    id: submission?.heraldData?.applicationId!,
    enabled: isDynamic,
  });

  const isHerlaldAndLoading = isHeraldLoading && isDynamic;

  const { items: teamProducts, isLoading: isLoadingProducts } = useSearchTeamInsuranceProduct({
    filter: {},
  });

  const initApiSelectedProducts = teamProducts.filter(
    (teamProduct) =>
      teamProduct.insuranceProduct.externalProductId &&
      !heraldApplicationResponse?.quote_exits?.some(
        (quote) => quote.product_id === teamProduct.insuranceProduct.externalProductId,
      ) &&
      heraldApplicationResponse?.application.products.includes(teamProduct.insuranceProduct.externalProductId),
  );

  const initApiSelectedProductsMap = initApiSelectedProducts.reduce((acc, product) => {
    acc[product.id] = product;
    return acc;
  }, {} as SelectedTeamProductsMap);

  if (isHerlaldAndLoading || isLoadingProducts) {
    return <LoaderOverlay />;
  }

  if (error || (!heraldApplicationResponse && isDynamic)) {
    return <Typography>{messages.errors.errorLoadingData}</Typography>;
  }

  return children({
    heraldApplicationResponse,
    isLoading: isHerlaldAndLoading,
    initSelectedProducts: isBOR ? {} : initApiSelectedProductsMap,
    teamProducts,
  });
}
